import { motion, useReducedMotion } from 'framer-motion'
import type { CSSProperties, ReactNode } from 'react'

import {
  buildRevealVariants,
  buildSlideInFromEndItemVariants,
  buildSlideInFromEndItemVariantsByIndex,
  buildStaggerContainerVariants,
  buildStaggerItemVariants,
  mergeReducedMotionPreset,
  resolveExpandTransition,
  resolveTransition,
  type StaggerMotionOptions,
  useThemeMotion,
} from '../../animation/themeMotion'

type CommonProps = {
  children: ReactNode
  className?: string
}

function reducedOpts(reduced: boolean | null) {
  return { reducedMotion: !!reduced }
}

export type StaggerEnterMode = 'default' | 'slideFromEnd'

type ThemeLayerElement = 'div' | 'aside' | 'section'

const themeLayerMotion: Record<ThemeLayerElement, typeof motion.div> = {
  div: motion.div,
  aside: motion.aside,
  section: motion.section,
}

/** 侧栏/画布等叠层显隐（与 CreatePage 分镜↔时间线切换同套 transition） */
export function ThemeLayerCrossfade({
  children,
  className,
  visible,
  initialX = 0,
  initialY = 0,
  style,
  as = 'div',
  /** 仅淡入淡出，不对层施加 transform（画布等内部 sticky 吸顶须开启） */
  fadeOnly = false,
}: CommonProps & {
  visible: boolean
  initialX?: number
  initialY?: number
  style?: CSSProperties
  as?: ThemeLayerElement
  fadeOnly?: boolean
}) {
  const preset = useThemeMotion()
  const reduced = useReducedMotion()
  const transition = resolveTransition(mergeReducedMotionPreset(preset, !!reduced))
  const Comp = themeLayerMotion[as]
  return (
    <Comp
      className={className}
      style={style}
      initial={
        fadeOnly
          ? { opacity: 0 }
          : { opacity: 0, x: initialX, y: initialY }
      }
      animate={
        fadeOnly
          ? { opacity: visible ? 1 : 0 }
          : { opacity: visible ? 1 : 0, x: 0, y: 0 }
      }
      transition={transition}
    >
      {children}
    </Comp>
  )
}

export function ThemeReveal({ children, className, delay = 0 }: CommonProps & { delay?: number }) {
  const preset = useThemeMotion()
  const reduced = useReducedMotion()
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={buildRevealVariants(preset, delay, reducedOpts(reduced))}
      className={className}
    >
      {children}
    </motion.div>
  )
}

export function ThemeStagger({
  children,
  className,
  delay = 0,
  staggerDirection = 1,
  /** 子项传 index 时：延迟 = index×stagger，容器不再 staggerChildren */
  indexStagger = false,
  /** 仅 1 个子项时不做容器整体淡入，避免固定槽位先空再载入 */
  singleChild,
}: CommonProps & {
  delay?: number
  staggerDirection?: 1 | -1
  indexStagger?: boolean
  singleChild?: boolean
}) {
  const preset = useThemeMotion()
  const reduced = useReducedMotion()
  const isSingle = singleChild === true
  const motionOpts: StaggerMotionOptions = {
    ...reducedOpts(reduced),
    staggerDirection,
    staggerChildren: indexStagger ? 0 : undefined,
    skipContainerFade: isSingle,
  }
  return (
    <motion.div
      initial={isSingle ? 'visible' : 'hidden'}
      animate="visible"
      variants={buildStaggerContainerVariants(preset, delay, motionOpts)}
      className={className}
    >
      {children}
    </motion.div>
  )
}

type StaggerItemProps = CommonProps & {
  style?: CSSProperties
  /** 显式序号时延迟为 index×主题 stagger；与容器 staggerChildren 二选一 */
  index?: number
  /**
   * default：主题 y+x 渐入；slideFromEnd：裁切容器内纯水平自右向左（推荐列表/栅格卡片）
   */
  enterMode?: StaggerEnterMode
  /**
   * 为 true 时启用布局平滑（如图库 masonry 容器变宽引起列数重排），动效时间与主题 expand 对齐。
   */
  layoutReflow?: boolean
  /** 为 true 时不播放入场（用于切换分页/选中分镜，仅新增项需要动画时传 false） */
  skipEnter?: boolean
}

export function ThemeStaggerItem({
  children,
  className,
  style,
  index,
  enterMode = 'default',
  layoutReflow = false,
  skipEnter = false,
}: StaggerItemProps) {
  const preset = useThemeMotion()
  const reduced = useReducedMotion()
  const motionOpts = reducedOpts(reduced)
  const layout = layoutReflow && !reduced
  const merged = mergeReducedMotionPreset(preset, !!reduced)
  const layoutEase = resolveExpandTransition(merged, { reducedMotion: !!reduced })

  const slideVariants =
    index != null
      ? buildSlideInFromEndItemVariantsByIndex(preset, motionOpts)
      : buildSlideInFromEndItemVariants(preset, motionOpts)
  const defaultVariants = buildStaggerItemVariants(preset, motionOpts)

  if (enterMode === 'slideFromEnd') {
    return (
      <motion.div
        layout={layout}
        transition={layout ? { layout: layoutEase } : undefined}
        className={['min-w-0 overflow-hidden', className].filter(Boolean).join(' ')}
        style={style}
      >
        <motion.div
          initial={skipEnter ? false : 'hidden'}
          animate="visible"
          variants={slideVariants}
          custom={index}
          className="min-w-0 w-full"
        >
          {children}
        </motion.div>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={skipEnter ? false : 'hidden'}
      animate="visible"
      variants={defaultVariants}
      custom={index}
      layout={layout}
      transition={layout ? { layout: layoutEase } : undefined}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  )
}
